import { ButtonEnum } from "designSystem/types/button-types";
import React, { FunctionComponent, useState } from "react";
import ErrorDialog from "../dialog/ErrorDialog";
import Button, { ButtonProps } from "./Button";

interface ValidationButtonProps extends Omit<ButtonProps, "onClick"> {
	/** callback to proceed */
	onAction: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void>;

	/** callback function to validate before proceeding */
	onValidate?: (event: React.MouseEvent<HTMLButtonElement>) => Promise<boolean>;
}

const ValidationButton: FunctionComponent<ValidationButtonProps> = (props) => {
	const [error, setError] = useState<Error>(null);
	const [saving, setSaving] = useState(false);

	const {
		disabled,
		onAction,
		onValidate,
		...otherProps
	} = props;

	async function _onClick(event: React.MouseEvent<HTMLButtonElement>) {
		setSaving(true);

		try {
			if (onValidate) {
				// need to go through validation process if configured
				const valid = await onValidate(event);

				if (valid) {
					// input is valid, proceed
					await onAction(event);
				}
			} else {
				await onAction(event);
			}
		} catch (err) {
			setError(err);
		} finally {
			setSaving(false);
		}
	}

	return (
		<>
			<Button
				design="primary"
				disabled={saving || disabled}
				fullWidth
				onClick={_onClick}
				{...otherProps}
			/>
			{error && (
				<ErrorDialog
					error={error}
					onClose={() => setError(null)}
					open
				/>
			)}
		</>
	);
};

export const FinishButton: FunctionComponent<ValidationButtonProps> = (props) => (
	<ValidationButton
		res={ButtonEnum.BUTTON_FINISH}
		{...props}
	/>
);

export interface SaveButtonProps extends ValidationButtonProps {
	/** custom label for the save button */
	labelRes?: string;
}

export const SaveButton: FunctionComponent<SaveButtonProps> = (props) => {
	const { labelRes, ...otherProps } = props;
	const finalLabel = labelRes || ButtonEnum.BUTTON_SAVE;

	return (
		<ValidationButton
			res={finalLabel}
			{...otherProps}
		/>
	);
};